<script setup lang="ts">
import WriteReviewForm from './WriteReviewForm.vue'
import ReviewCard from './ReviewCard.vue'

interface Review {
  _id: string
  company: {
    permalink: string
    brand_name: string
    logo_url?: string
  }
  title: string
  text: string
  overall_rating: number
  created_at: string
  updated_at: string
  rating_details: { [key: string]: number }
  status: string
  date: Date
  author: {
    name: string
    avatar_url?: string
  }
  isUserEditable?: boolean
}
const props = defineProps<{
  reviews: Review[]
  isEditable?: boolean
  companyName?: string
  permalink: string
  userReview?: object | null
  logoUrl?: string
  userReviewId?: string
}>()
const emit = defineEmits([
  'edit-review',
  'delete',
  'write-review',
  'review-updated'
])
const editingReviewId = ref(null)
const isSubmitting = ref(false)
const toast = useToast()
const findReviewById = (id) => {
  return props.reviews.find(review => review._id === id)
}
const isUserReview = (reviewId) => {
  if (props.userReviewId) {
    return reviewId === props.userReviewId
  }
  const review = findReviewById(reviewId)
  return review && review.isUserEditable === true
}
const isEditingReview = (reviewId) => {
  return editingReviewId.value === reviewId
}
const startEditing = (reviewId) => {
  if (!isUserReview(reviewId)) {
    return
  }
  editingReviewId.value = reviewId
}
const handleDelete = (reviewId) => {
  if (!isUserReview(reviewId)) {
    return
  }
  emit('delete', reviewId)
}
const handleSubmitReview = async (formData) => {
  if (!editingReviewId.value) {
    console.error('No review is being edited')
    return
  }
  isSubmitting.value = true
  try {
    const editingReview = findReviewById(editingReviewId.value)
    if (!editingReview) {
      throw new Error('Review not found')
    }
    const { session } = useUserSession()
    const requestBody = {
      ...formData,
      review_id: editingReviewId.value,
      user_id: session.value?.user?.id,
      content: formData.content || formData.text,
      rating: formData.rating || formData.overall_rating
    }
    const response = await $fetch(
      `/api/companies/${editingReview.company.permalink}/reviews/update`,
      {
        method: 'POST',
        body: requestBody
      }
    )
    if (response.success) {
      toast.add({
        title: 'Success',
        description: response.message || 'Review updated successfully',
        color: 'green'
      })
      editingReviewId.value = null
      emit('review-updated')
    } else {
      throw new Error(response.message || 'Failed to update review')
    }
  } catch (error) {
    console.error('Error updating review:', error)
    toast.add({
      title: 'Error',
      description: error.message || 'Failed to update review',
      color: 'red'
    })
  } finally {
    isSubmitting.value = false
  }
}
const closeEditForm = () => {
  editingReviewId.value = null
}
defineExpose({
  startEditing
})
</script>

<template>
  <div>
    <div class="columns-1 sm:columns-2 gap-4">
      <div
        v-if="editingReviewId"
        class="col-span-full w-full mb-6"
      >
        <WriteReviewForm
          v-if="editingReviewId"
          :is-submitting="isSubmitting"
          :initial-data="findReviewById(editingReviewId)"
          :is-update="true"
          :permalink="findReviewById(editingReviewId)?.company.permalink"
          :is-in-grid="true"
          :show-preview="true"
          class="w-full h-auto"
          @submit="handleSubmitReview"
          @close="closeEditForm"
        />
      </div>
      <div
        v-for="review in reviews"
        v-show="!editingReviewId || review._id !== editingReviewId"
        :key="review._id"
        class="inline-block w-full break-inside-avoid mb-4"
      >
        <ReviewCard
          :review="review"
          :is-editable="isEditable && isUserReview(review._id)"
          @delete="handleDelete(review._id)"
          @edit="startEditing(review._id)"
        />
      </div>
    </div>
  </div>
</template>

